import React from 'react';
import { ChevronDown, ChevronRight, Trash2, Database, Code } from 'lucide-react';

interface SchemaField {
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'reference';
  required?: boolean;
  description?: string;
  referenceCollection?: string;
  arrayItemType?: SchemaField;
  objectSchema?: Record<string, SchemaField>;
}

interface SchemaFieldEditorProps {
  fieldName: string;
  field: SchemaField;
  depth?: number;
  parentPath?: string;
  expandedFields: Set<string>;
  onToggleExpansion: (fieldPath: string) => void;
  onUpdateField: (fieldPath: string, updates: Partial<SchemaField>) => void;
  onRemoveField: (fieldName: string) => void;
  onAddNestedField: (parentPath: string) => void;
  createSchemaField: (type: SchemaField['type']) => SchemaField;
}

const SchemaFieldEditor: React.FC<SchemaFieldEditorProps> = ({
  fieldName,
  field,
  depth = 0,
  parentPath = '',
  expandedFields,
  onToggleExpansion,
  onUpdateField,
  onRemoveField,
  onAddNestedField,
  createSchemaField
}) => {
  const currentPath = parentPath ? `${parentPath}.${fieldName}` : fieldName;
  const isExpanded = expandedFields.has(currentPath);
  const hasNested = (field.type === 'array' && field.arrayItemType) || (field.type === 'object' && field.objectSchema);
  
  // Schema field type colors
  const getTypeColor = (type: string) => {
    const colors = {
      string: '#10b981',
      number: '#3b82f6',
      boolean: '#f59e0b',
      date: '#8b5cf6',
      array: '#ef4444',
      object: '#6366f1',
      reference: '#ec4899'
    };
    return colors[type as keyof typeof colors] || '#6b7280';
  };

  return (
    <div 
      className={`schema-field-card ${depth === 0 ? 'top-level' : 'nested'}`}
      style={{
        border: '2px solid #f3f4f6',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        marginLeft: `${depth * 24}px`,
        backgroundColor: depth === 0 ? '#ffffff' : '#fafafa',
        boxShadow: depth === 0 ? '0 2px 4px rgba(0,0,0,0.04)' : 'none',
        transition: 'all 0.2s ease',
        position: 'relative'
      }}
    >
      {/* Field Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {hasNested && (
            <button
              onClick={() => onToggleExpansion(currentPath)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                marginRight: '8px',
                padding: '4px',
                borderRadius: '4px',
                color: '#6b7280',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <h4 style={{ 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: '600',
                color: '#1f2937'
              }}>
                {fieldName}
              </h4>
              <span style={{
                fontSize: '12px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: getTypeColor(field.type),
                padding: '2px 8px',
                borderRadius: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {field.type}
              </span>
              {field.required && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#dc2626',
                  backgroundColor: '#fee2e2',
                  padding: '2px 6px',
                  borderRadius: '8px'
                }}>
                  Required
                </span>
              )}
            </div>
            {field.description && (
              <p style={{ 
                margin: 0, 
                fontSize: '14px', 
                color: '#6b7280',
                fontStyle: 'italic'
              }}>
                {field.description}
              </p>
            )}
          </div>
        </div>
        {depth === 0 && (
          <button
            onClick={() => onRemoveField(fieldName)}
            style={{
              color: '#ef4444',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#fee2e2';
              e.currentTarget.style.borderColor = '#fca5a5';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#fef2f2';
              e.currentTarget.style.borderColor = '#fecaca';
            }}
            title="Remove field"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Field Configuration */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '16px'
      }}>
        {/* Type Selection */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#374151',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Type
          </label>
          <select
            value={field.type}
            onChange={(e) => onUpdateField(currentPath, { type: e.target.value as SchemaField['type'] })}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          >
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="date">Date</option>
            <option value="array">Array</option>
            <option value="object">Object</option>
            <option value="reference">Reference</option>
          </select>
        </div>

        {/* Required Toggle */}
        <div style={{ display: 'flex', alignItems: 'end' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontSize: '14px', 
            fontWeight: '500',
            cursor: 'pointer',
            padding: '10px 12px',
            backgroundColor: field.required ? '#dbeafe' : '#f9fafb',
            border: '2px solid',
            borderColor: field.required ? '#93c5fd' : '#e5e7eb',
            borderRadius: '8px',
            transition: 'all 0.2s ease'
          }}>
            <input
              type="checkbox"
              checked={field.required || false}
              onChange={(e) => onUpdateField(currentPath, { required: e.target.checked })}
              style={{ 
                marginRight: '8px',
                width: '16px',
                height: '16px',
                accentColor: '#3b82f6'
              }}
            />
            Required Field
          </label>
        </div>

        {/* Reference Collection (for reference type) */}
        {field.type === 'reference' && (
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#374151',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Reference Collection
            </label>
            <input
              type="text"
              value={field.referenceCollection || ''}
              onChange={(e) => onUpdateField(currentPath, { referenceCollection: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              placeholder="Collection name"
            />
          </div>
        )}
      </div>

      {/* Description Field */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151',
          marginBottom: '6px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Description
        </label>
        <input
          type="text"
          value={field.description || ''}
          onChange={(e) => onUpdateField(currentPath, { description: e.target.value })}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            transition: 'border-color 0.2s ease'
          }}
          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          placeholder="Describe this field..."
        />
      </div>

      {/* Array Item Type Configuration */}
      {field.type === 'array' && (
        <div style={{ 
          marginTop: '16px',
          padding: '16px',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          border: '1px solid #fde68a'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ 
              fontSize: '14px', 
              fontWeight: '600',
              color: '#92400e',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Database size={16} style={{ marginRight: '6px' }} />
              Array Item Type
            </label>
            {!field.arrayItemType && (
              <button
                onClick={() => onUpdateField(currentPath, { 
                  arrayItemType: createSchemaField('string') 
                })}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
              >
                Define Item Type
              </button>
            )}
          </div>
          
          {field.arrayItemType && isExpanded && (
            <SchemaFieldEditor 
              fieldName="arrayItemType" 
              field={field.arrayItemType} 
              depth={depth + 1} 
              parentPath={currentPath}
              expandedFields={expandedFields}
              onToggleExpansion={onToggleExpansion}
              onUpdateField={onUpdateField}
              onRemoveField={onRemoveField}
              onAddNestedField={onAddNestedField}
              createSchemaField={createSchemaField}
            />
          )}
        </div>
      )}

      {/* Object Schema Configuration */}
      {field.type === 'object' && (
        <div style={{ 
          marginTop: '16px',
          padding: '16px',
          backgroundColor: '#ede9fe',
          borderRadius: '8px',
          border: '1px solid #c4b5fd'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ 
              fontSize: '14px', 
              fontWeight: '600',
              color: '#6d28d9',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Code size={16} style={{ marginRight: '6px' }} />
              Object Properties ({Object.keys(field.objectSchema || {}).length})
            </label>
            <button
              onClick={() => onAddNestedField(currentPath)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
            >
              Add Property
            </button>
          </div>
          
          {field.objectSchema && isExpanded && (
            <div style={{ marginTop: '12px' }}>
              {Object.entries(field.objectSchema).map(([propName, propField]) =>
                <SchemaFieldEditor 
                  key={propName}
                  fieldName={propName} 
                  field={propField} 
                  depth={depth + 1} 
                  parentPath={currentPath}
                  expandedFields={expandedFields}
                  onToggleExpansion={onToggleExpansion}
                  onUpdateField={onUpdateField}
                  onRemoveField={onRemoveField}
                  onAddNestedField={onAddNestedField}
                  createSchemaField={createSchemaField}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SchemaFieldEditor;